import { ChangeEvent, FocusEventHandler, useState } from "react"

import { CellProps, Col } from "components/Rable/types"
import { css, cx } from "@emotion/css"

import { ColorMap } from "utils/color"
import { CreativeAgency } from "models/creativeAgencies"
import GhostNumericInput from "ui/inputs/GhostNumericInput"
import GhostTextInput from "ui/inputs/GhostTextInput"
import { Status } from "components/Status"
import WarningIcon from "@material-ui/icons/Error"
import get from "lodash/get"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

function StatusCell(props: CellProps<CreativeAgency>) {
  const { row, name, getValue } = props
  const value = getValue ? getValue(row) : get(row, name, "")

  const { t } = useI18n()
  return name === null ? null : (
    <Status color={ColorMap[value]}>{t(`status.${value ?? ""}`)}</Status>
  )
}

function EditableCell<T extends Record<string, any>>(props: CellProps<T>) {
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const {
    row,
    rowId,
    name,
    getValue,
    getOutput,
    getError,
    onUpdate,
    clearError,
  } = props
  const error = getError?.(rowId, name) ?? ""

  const initialValue = getValue ? getValue(row) : get(row, name, "")
  const [currentValue, setCurrentValue] = useState(initialValue)

  const onBlur: FocusEventHandler<HTMLInputElement> = (evt) => {
    const hasValueDiff = initialValue?.toString() !== evt.target.value
    const output = getOutput
      ? getOutput(evt.target.value)
      : ({ [name]: evt.target.value } as Partial<T>)
    hasValueDiff && onUpdate(rowId, output)
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setCurrentValue(value)
    !!clearError && clearError(rowId, name)
  }

  const placeholder = t(`market.creativeAgencies.add.new.${name as string}`)

  const disabled =
    !hasAuthority(["AUTH_EDIT_CREATIVE_AGENCY"]) ||
    row?.status === "ACTIVE" ||
    row?.status === "INACTIVE"

  return !disabled ? (
    <>
      <GhostTextInput
        value={currentValue}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={onBlur}
        className={cx(styles.input, styles.ghostInput, {
          [styles.error]: !!error,
        })}
      />
      {error ? (
        <div
          className={css`
            position: absolute;
            top: 0;
            color: var(--red);
          `}
          title={t(error)}
        >
          <WarningIcon fontSize="small" color="inherit" />
        </div>
      ) : null}
    </>
  ) : (
    <div className={cx("BodyBody-2BlackRegular", styles.readonly)}>
      {initialValue as number}
    </div>
  )
}

export function NumCell<T extends Record<string, any>>(props: CellProps<T>) {
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const { row, rowId, name, getValue, getError, onUpdate, clearError } = props
  const error = getError?.(rowId, name) ?? ""
  const initialValue = getValue ? getValue(row) : get(row, name, "")
  const [currentValue, setCurrentValue] = useState(initialValue)

  const onBlur: FocusEventHandler<HTMLInputElement> = () => {
    const hasValueDiff = initialValue.toString() !== currentValue

    hasValueDiff && onUpdate(rowId, { [name]: currentValue } as Partial<T>)
  }

  const handleChange = (value: string) => {
    setCurrentValue(value)
    !!clearError && clearError(rowId, name)
  }

  const placeholder = t(`market.creativeAgencies.add.new.${name as string}`)

  const disabled =
    !hasAuthority(["AUTH_EDIT_CREATIVE_AGENCY"]) ||
    row?.status === "ACTIVE" ||
    row?.status === "INACTIVE"

  return (
    <>
      <GhostNumericInput
        name={`${row?.id}.${name as string}`}
        placeholder={placeholder}
        value={currentValue}
        disabled={disabled}
        onInputChange={handleChange}
        onBlur={onBlur}
        className={cx(styles.input, styles.ghostInput, {
          [styles.error]: !!error,
        })}
      />
      {error ? (
        <>
          <div
            className={css`
              position: absolute;
              top: 0;
              color: var(--red);
            `}
            title={t(error)}
          >
            <WarningIcon fontSize="small" color="inherit" />
          </div>
        </>
      ) : null}
    </>
  )
}

const styles = {
  readonly: css`
    margin: var(--spacing-xxs);
    display: flex;
    flex: 1;
  `,
  input: css`
    font-size: 13px;
    :hover {
      cursor: pointer;
    }
    .MuiOutlinedInput-input {
      font-size: 13px;
    }
  `,
  ghostInput: css`
    padding: var(--spacing-xxs);
    :focus-within {
      border: 2px solid var(--primary-main, teal);
    }
    width: 100%;
    height: 85%;
  `,
  wrap: css`
    display: flex;
    flex: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  error: css`
    border: 1px solid var(--red);
  `,
}

export const COLUMNS: Col<CreativeAgency>[] = [
  {
    id: "Creative Agency",
    name: "name",
    cell: EditableCell,
    newRowCell: EditableCell,
    width: "20.5%",
    existsInCreate: true,
    required: true,
  },
  {
    id: "RUT",
    name: "rut",
    cell: EditableCell,
    newRowCell: EditableCell,
    width: "20.5%",
    existsInCreate: true,
    required: true,
  },
  {
    id: "Code",
    name: "code",
    cell: NumCell,
    newRowCell: NumCell,
    width: "20.5%",
    existsInCreate: true,
    required: true,
  },
  {
    id: "Status",
    name: "status",
    cell: StatusCell,
    newRowCell: StatusCell,
    width: "20.5%",
    existsInCreate: false,
    required: false,
  },
]
